/**
 * Parse checklist question files (CSV / JSON) in the browser for import preview.
 * Expected CSV headers (any order; aliases accepted):
 * question, instruction, inspection_criteria|criteria, category, method_tool|method,
 * unit, value_type|type, pass_threshold|threshold, item_code, level
 */

const HEADER_ALIASES = {
  question: ['question', 'inspection_item', 'inspection item', 'item', 'detailed item', 'title'],
  instruction: ['instruction', 'how', 'how_to', 'basis', 'basis & regulatory reference'],
  inspection_criteria: [
    'inspection_criteria',
    'criteria',
    'standard',
    'standard_ref',
    'pass criteria',
    'threshold text',
  ],
  category: ['category', 'cat'],
  method_tool: ['method_tool', 'method', 'tool', 'method / tool'],
  unit: ['unit'],
  value_type: ['value_type', 'type', 'answer_type'],
  pass_threshold: ['pass_threshold', 'threshold', 'pass if'],
  item_code: ['item_code', 'id', 'code'],
  level: ['level', 'lv'],
};

function normHeader(h) {
  return String(h || '')
    .trim()
    .toLowerCase()
    .replace(/^\ufeff/, '');
}

function mapHeader(h) {
  const n = normHeader(h);
  for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
    if (aliases.some((a) => a === n)) return field;
  }
  return null;
}

function parseCsv(text) {
  const rows = [];
  let i = 0;
  const s = String(text || '').replace(/^\ufeff/, '');
  while (i < s.length) {
    const row = [];
    while (i < s.length) {
      let cell = '';
      if (s[i] === '"') {
        i += 1;
        while (i < s.length) {
          if (s[i] === '"' && s[i + 1] === '"') {
            cell += '"';
            i += 2;
            continue;
          }
          if (s[i] === '"') {
            i += 1;
            break;
          }
          cell += s[i++];
        }
      } else {
        while (i < s.length && s[i] !== ',' && s[i] !== '\n' && s[i] !== '\r') {
          cell += s[i++];
        }
      }
      row.push(cell.trim());
      if (s[i] === ',') {
        i += 1;
        continue;
      }
      if (s[i] === '\r') i += 1;
      if (s[i] === '\n') i += 1;
      break;
    }
    if (row.some((c) => c.length)) rows.push(row);
  }
  return rows;
}

function normalizeValueType(v) {
  const t = String(v || 'BOOL').trim().toUpperCase();
  if (t === 'NUM' || t === 'NUMBER' || t === 'N') return 'NUM';
  if (t === 'TEXT' || t === 'NOTE' || t === 'NOTES' || t === 'T') return 'TEXT';
  if (t === 'BOOL' || t === 'BOOLEAN' || t === 'P/F' || t === 'PF' || t === 'B') return 'BOOL';
  return 'BOOL';
}

function rowToItem(obj, index) {
  const question = String(obj.question || '').trim();
  if (!question) return null;
  return {
    id: `import-${Date.now()}-${index}`,
    question,
    instruction: String(obj.instruction || '').trim(),
    inspection_criteria: String(obj.inspection_criteria || '').trim(),
    category: String(obj.category || '').trim() || null,
    method_tool: String(obj.method_tool || '').trim() || null,
    unit: String(obj.unit || '').trim() || '—',
    value_type: normalizeValueType(obj.value_type),
    pass_threshold: String(obj.pass_threshold || '').trim() || null,
    item_code: String(obj.item_code || '').trim() || null,
    level: obj.level != null && obj.level !== '' ? Number(obj.level) : null,
    source: 'CUSTOM',
    origin: 'IMPORT',
    field_added: false,
  };
}

export function parseChecklistImportText(text, fileName = '') {
  const name = String(fileName || '').toLowerCase();
  const raw = String(text || '').trim();
  if (!raw) return { items: [], warnings: ['File is empty'] };

  const warnings = [];

  if (name.endsWith('.json') || raw.startsWith('[') || raw.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw);
      const list = Array.isArray(parsed) ? parsed : parsed.items || parsed.questions || [];
      if (!Array.isArray(list)) {
        return { items: [], warnings: ['JSON must be an array of questions'] };
      }
      const items = list.map((row, i) => rowToItem(row, i)).filter(Boolean);
      if (!items.length) warnings.push('No valid question rows in JSON');
      return { items, warnings };
    } catch (e) {
      return { items: [], warnings: [`Invalid JSON: ${e.message}`] };
    }
  }

  const table = parseCsv(raw);
  if (table.length < 2) {
    return { items: [], warnings: ['CSV needs a header row and at least one data row'] };
  }
  const headerMap = table[0].map(mapHeader);
  if (!headerMap.includes('question')) {
    warnings.push('No “question” column found ?? using first column as question title');
  }
  const items = [];
  for (let r = 1; r < table.length; r++) {
    const obj = {};
    table[0].forEach((_, col) => {
      const field = headerMap[col] || (col === 0 && !headerMap.includes('question') ? 'question' : null);
      if (field) obj[field] = table[r][col] ?? '';
    });
    const item = rowToItem(obj, r);
    if (item) items.push(item);
  }
  if (!items.length) warnings.push('No valid question rows in CSV');
  return { items, warnings };
}

export function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Read failed'));
    reader.readAsText(file);
  });
}
